import { AxiosError } from "axios";
import { Button } from "components/Button";
import { Dialog } from "components/Dialog";
import { TextField } from "components/Forms/TextField";
import { LeadCard } from "models/lead";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { updateLeadData } from "services/leadService";
import { Log } from "utils/log";

export type LeadForm = {
  selling: string;
};

type Props = {
  lead: LeadCard;
  onSave: (lead: LeadCard) => void;
  onClose: () => void;
};

export const LeadSellingForm = ({ lead, onSave, onClose }: Props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LeadForm>({
    defaultValues: {
      selling: lead.selledAt ?? new Date().toISOString().split("T")[0],
    },
  });

  const onSubmit = async (data: LeadForm) => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      await updateLeadData(lead.id, {
        name: lead.name,
        socialOrigin: lead.socialOrigin,
        seller: lead.seller?.id ?? undefined,
        selledAt: data.selling,
        description: lead.description,
      });
      onSave({
        ...lead,
        ...data,
        selledAt: data.selling,
      });
    } catch (error) {
      const _error = error as AxiosError;

      if (_error.isAxiosError) {
        const { status } = _error.response!;

        if (status === 403) {
          toast.error(
            t("leads.board.errors.already_attributed_seller", {
              name: lead.name,
            })
          );
          Log.error("Error on update lead", _error);
          return;
        }
      }

      toast.error(t("leads.board.errors.edit_lead", { name: lead.name }));
      Log.error("Error on update lead", error as Error);
    }

    setIsLoading(false);
  };

  return (
    <Dialog onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        <TextField
          register={register("selling", {
            required: {
              value: true,
              message: t("leads.board.lead_selling_form.selling_date_required"),
            },
            //Check if is a date
            validate: (value) => {
              const date = new Date(value);
              return date.toString() !== "Invalid Date";
            },
          })}
          placeholder={t(
            "leads.board.lead_selling_form.selling_date_placeholder"
          )}
          label={t("leads.board.lead_selling_form.selling_date")}
          error={errors.selling?.message}
          type="date"
          variant="secondary"
        />
        <div className="flex items-center justify-end gap-2 mt-4">
          <Button theme="cancel" type="button" onClick={onClose}>
            {t("actions.cancel")}
          </Button>
          <Button theme="highlight" className="px-5" loading={isLoading}>
            {t("actions.edit")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
